import { Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { CompareHelper } from '@barthauer/abc/core';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { _AbcContextMenuComponent, _AbcToolbarComponent, _AbcTreeComponent } from '@barthauer/abc/ui-components';
import { Item as ContextMenuItem } from 'devextreme/ui/context_menu';
import { OpenedStateMode, OptionChangedEvent, RevealMode } from 'devextreme/ui/drawer';
import { Item as TreeItem } from 'devextreme/ui/tree_view';
import { DataService } from './services/data.service';

@Component({
  selector: 'custom-arena',
  templateUrl: './custom-arena.component.html',
  styleUrls: ['./custom-arena.component.scss'],
  styles: [],
  standalone: false,
})
export class CustomArenaComponent implements OnInit {
  public abcToolbar = viewChild<_AbcToolbarComponent>('abcToolbar');
  public avatarImage = viewChild<ElementRef<HTMLImageElement>>('avatarImage');
  public treeView = viewChild<_AbcTreeComponent>('treeView');
  @ViewChild('contextMenu') contextMenu: _AbcContextMenuComponent;
  @Input() contextMenuAdditionalItems: ContextMenuItem[] = [];
  @Input() currentUserName: string;
  @Input() firstLogo: string;
  @Input() locationMenu: boolean = false;
  @Input() loginLogo: string;
  @Input() logoIcon: string;
  @Input() logoMiniatureIcon: string;
  @Input() logoNavigationBorder: string;
  @Input() logoutIcon = 'assets/images/logout.svg';
  @Input() logoutText = 'Ausloggen';
  @Input() menuOpened: boolean = true;
  @Input() maxMenuSize: string;
  @Input() navigationBorderFirstTitle: string;
  @Input() navigationBorderSecondTitle: string;
  @Input() navigationCollapseTooltipText: string = '';
  @Input() navigationExpandTooltipText: string = '';
  @Input() navigationItems: Array<TreeItem> = [];
  @Input() secondLogo: string;
  @Input() showBackButton = false;
  @Input() showLogout = true;
  @Input() showMenuToggleButton = false;
  @Input() showNavigationBorder = true;
  @Input() showToggleButton = false;
  @Input() showTreeViewScrollContainer: boolean = false;
  @Input() toolBarFirstTitle: string;
  @Input() toolBarSecondTitle: string;
  @Input() updateToolBarTitles = true;
  @Input() userAvatar: string;
  @Input() userFunction: string;
  @Input() userMessage = true;
  @Input() userNotification = true;
  @Input() visiblityDrawer = true;
  @Input() showMobileToggleIcon: boolean = false;
  @Input() visiblityLogo: boolean = false;
  @Input() customAvatar: boolean = false;
  @Input() defaultAvatar: string = 'assets/images/profile.svg';

  @Output() avatarClick = new EventEmitter<void>();
  @Output() getLoginInfo: EventEmitter<any> = new EventEmitter();
  @Output() menuToggle: EventEmitter<boolean> = new EventEmitter();
  @Output() onCloseOnOutsideClick: EventEmitter<boolean> = new EventEmitter();
  @Output() onNavigationItemClick: EventEmitter<any> = new EventEmitter();

  public contextMenuItems: TreeItem;
  public displayGoBackButton: boolean;
  public flexValue = '0';
  public menuMode: OpenedStateMode = 'shrink';
  public menuRevealMode: RevealMode = 'expand';
  public minMenuSize = 0;
  public res = ResourceSet.Empty;
  public resAsync: Promise<ResourceSet>;
  public shaderEnabled = false;
  public _showMobileToggleIcon = false;

  private _temporaryMenuOpened = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private location: Location,
    private router: Router,
    private dataService: DataService,
    private resourceService: ResourceManagerService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('custom-arena');
  }

  async ngOnInit(): Promise<void> {
    this.updateDrawer();

    this.res = await this.resAsync;
    this.contextMenuItems = [
      {
        text: 'Logout',
        icon: this.res?.svgUrl('Logout-Image'),
        onClick: () => {
          this.logout();
        },
      },
    ];
    if (!CompareHelper.isEmptyObject(this.contextMenuAdditionalItems)) {
      this.contextMenuItems = [...this.contextMenuAdditionalItems];
    }

    if (this.showLogout) {
      this.flexValue = '10';
    } else {
      this.flexValue = '0';
    }
    await this.initializeUserInfo();
    this.cdr.markForCheck();
  }

  @HostListener('window:resize', ['$event'])
  public updateDrawer() {
    const newWidth = window.innerWidth;
    const isSmall = newWidth <= 750;
    this.menuMode = isSmall ? 'overlap' : 'shrink';
    this.menuRevealMode = isSmall ? 'slide' : 'expand';
    this.minMenuSize = isSmall ? 0 : 60;
    this.shaderEnabled = isSmall;
    if (this.showMobileToggleIcon) {
      this._showMobileToggleIcon = isSmall;
    }

    setTimeout(() => {
      this.abcToolbar()?.instance._refresh();
      this.treeView()?.instance._refresh();
    }, 100);
  }

  private async initializeUserInfo(): Promise<void> {
    const userInfos = await this.dataService.getUserInformations();
    if (userInfos.person) {
      this.userAvatar = this.userAvatar ?? (userInfos?.person.ImgPortrait as string);
      this.currentUserName =
        this.currentUserName ?? [userInfos?.person.FirstName, userInfos?.person.LastName].filter(Boolean).join(' ');
      this.userFunction = this.userFunction ?? userInfos?.person.CompanyName;
      this.getLoginInfo.emit(userInfos);
    } else {
      this.currentUserName = this.currentUserName ?? (userInfos?.loginInfo.displayName as string);
    }
  }

  public updateProfilePicture(picture: string): void {
    this.userAvatar = picture;
  }

  public onDrawerOptionChanged(event: OptionChangedEvent): void {
    if (event.name === 'opened' && event.value === false) {
      this.onCloseOnOutsideClick.emit(true);
    }
  }

  public toggleMenu(e: MouseEvent): void {
    this.menuOpened = !this.menuOpened;
    this.menuToggle.emit(this.menuOpened);

    e.stopPropagation();
  }

  public logout(): void {
    this.router.navigateByUrl('/login');
    localStorage.clear();
    sessionStorage.clear();
  }

  public onMenuItemClick(event: any): void {
    if (!event.itemData.items) {
      if (this.updateToolBarTitles) {
        this.toolBarSecondTitle = event.itemData.toolbarTitle || event.itemData.text;
      }
      if (this.showMenuAfterClick) {
        this._temporaryMenuOpened = true;
        this.menuOpened = true;
      }
      const path = event.itemData.path;
      const pointerEvent = event.event;
      if (path && this.menuOpened) {
        this.onNavigationItemClick.emit(event);
        if (this.hideMenuAfterNavigation) {
          this._temporaryMenuOpened = false;
          this.menuOpened = false;
          pointerEvent.stopPropagation();
        }
      } else {
        pointerEvent.preventDefault();
      }
      this.treeView().instance.selectItem(event.itemData.path);
    }
  }

  get showMenuAfterClick(): boolean {
    return !this.menuOpened;
  }

  get hideMenuAfterNavigation(): boolean {
    return this.menuMode === 'overlap' || this._temporaryMenuOpened;
  }

  public goBack(): void {
    this.location.back();
  }

  public onAvatarClick(): void {
    this.avatarClick.emit();
  }

  public onToolbarItemChanged(title: string) {
    this.toolBarSecondTitle = title;
    this.cdr.detectChanges();
  }
}
